import {
    Button,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Input,
    Modal,
    ModalBody,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    Text,
    useToast,
    VStack,
    HStack,
    Code,
    IconButton,
    Tooltip,
    Select,
} from "@chakra-ui/react";
import React from "react";
import { FunctionComponent, useState } from "react";
import { MODAL_CONTENT_SIZING, MODAL_SIZING } from "../utils/constants";
import { useForm } from "react-hook-form";
import { IconCopy, IconDownload, IconCheck } from "@tabler/icons-react";
import { createApiKeyAsync, CreateApiKeyRequest, CreateApiKeyResponse } from "../api/api";
import { useAvailableRegions } from "../api/transcriptionApiQueries";

interface CreateApiKeyModalProps {
    show: boolean;
    onClose: () => void;
    onSuccess?: () => void;
}

type FormFields = {
    name: string;
    prefix: string;
    email: string;
    owner: string;
    region: string;
    sbu?: string;
};

type ApiKeyCreationResult = CreateApiKeyResponse;

export const CreateApiKeyModal: FunctionComponent<CreateApiKeyModalProps> = ({ show, onClose, onSuccess }) => {
    const [isCreating, setIsCreating] = useState(false);
    const [createdApiKey, setCreatedApiKey] = useState<ApiKeyCreationResult | null>(null);
    const [hasConfirmedSave, setHasConfirmedSave] = useState(false);
    const [copiedToClipboard, setCopiedToClipboard] = useState(false);
    const [hasInteractedWithKey, setHasInteractedWithKey] = useState(false);
    const toast = useToast();
    const { data: regions, isLoading, error } = useAvailableRegions();

    const formValues: FormFields = {
        name: "",
        prefix: "",
        email: "",
        owner: "",
        region: regions?.defaultRegion || "",
        sbu: "",
    };

    const {
        handleSubmit,
        register,
        formState: { errors },
        reset,
    } = useForm<FormFields>({ values: formValues });

    const prefixRegistration = register("prefix", {
        required: "Prefix is required",
        pattern: {
            value: /^[a-z0-9]+$/,
            message: "Prefix can only contain lowercase letters and numbers",
        },
    });

    const onSubmit = async (data: FormFields) => {
        setIsCreating(true);

        try {
            const requestData: CreateApiKeyRequest = {
                name: data.name,
                prefix: data.prefix.toLowerCase(), // Ensure lowercase
                email: data.email,
                owner: data.owner,
                sbu: data.sbu || undefined,
                region: data.region,
            } as CreateApiKeyRequest;

            const response = await createApiKeyAsync(requestData);

            setCreatedApiKey(response);
            setIsCreating(false);

            // Notify parent component of successful creation
            onSuccess?.();
        } catch (error) {
            setIsCreating(false);
            const errorMessage = error instanceof Error ? error.message : "Failed to create API key";
            toast({
                title: "Error",
                description: errorMessage,
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        }
    };

    const handleCopyToClipboard = () => {
        if (createdApiKey) {
            navigator.clipboard.writeText(createdApiKey.apiKey);
            setCopiedToClipboard(true);
            setHasInteractedWithKey(true);
            toast({
                title: "Copied!",
                description: "API key copied to clipboard",
                status: "success",
                duration: 2000,
                isClosable: true,
            });
            setTimeout(() => setCopiedToClipboard(false), 2000);
        }
    };

    const handleDownloadKey = () => {
        if (createdApiKey) {
            const blob = new Blob([createdApiKey.apiKey], { type: "text/plain" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `${createdApiKey.name.replace(/\s+/g, "_")}_api_key.txt`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            setHasInteractedWithKey(true);
            toast({
                title: "Downloaded!",
                description: "API key saved to file",
                status: "success",
                duration: 2000,
                isClosable: true,
            });
        }
    };

    const handleManualCopy = (event: React.ClipboardEvent) => {
        const selection = (event.currentTarget as HTMLElement).innerText || window.getSelection()?.toString();

        if (selection && createdApiKey && selection.includes(createdApiKey.apiKey)) {
            setHasInteractedWithKey(true);
        }
    };

    const handleClose = () => {
        if (createdApiKey && !hasConfirmedSave) {
            toast({
                title: "Warning",
                description: "Please confirm you have saved your API key before closing",
                status: "warning",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        // Reset state
        reset();
        setCreatedApiKey(null);
        setHasConfirmedSave(false);
        setCopiedToClipboard(false);
        setHasInteractedWithKey(false);
        onClose();
    };

    const handleConfirmSave = () => {
        setHasConfirmedSave(true);
        handleClose();
    };

    return (
        <Modal isOpen={show} onClose={handleClose} size={MODAL_SIZING} isCentered motionPreset="slideInBottom" closeOnOverlayClick={!createdApiKey}>
            <ModalOverlay />
            <ModalContent style={{ ...MODAL_CONTENT_SIZING }} overflowY="auto">
                <ModalHeader>{createdApiKey ? "API Key Created Successfully" : "Create new API Key"}</ModalHeader>
                <ModalBody>
                    {!createdApiKey ? (
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <VStack spacing={4} mb="2rem">
                                <FormControl isRequired isInvalid={!!errors.name}>
                                    <FormLabel>Application Name</FormLabel>
                                    <Input {...register("name", { required: "Name is required" })} placeholder="My Application" autoFocus />
                                    <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
                                </FormControl>

                                <FormControl isRequired isInvalid={!!errors.prefix}>
                                    <FormLabel>API Key Prefix</FormLabel>
                                    <Input
                                        {...prefixRegistration}
                                        onChange={event => {
                                            event.target.value = event.target.value.toLowerCase();
                                            prefixRegistration.onChange(event);
                                        }}
                                        placeholder="myapp"
                                        autoCapitalize="none"
                                        spellCheck={false}
                                    />
                                    <FormErrorMessage>{errors.prefix?.message}</FormErrorMessage>
                                    <Text fontSize="sm" color="gray.500" mt={1}>
                                        Must be unique, lowercase, and can only contain letters and numbers
                                    </Text>
                                </FormControl>

                                <FormControl isRequired isInvalid={!!errors.email}>
                                    <FormLabel>Created By</FormLabel>
                                    <Input
                                        {...register("email", {
                                            required: "Creator email is required",
                                            pattern: {
                                                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                                message: "Invalid email address",
                                            },
                                        })}
                                        type="email"
                                        placeholder="your@email.com"
                                    />
                                    <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
                                </FormControl>

                                <FormControl isRequired isInvalid={!!errors.owner}>
                                    <FormLabel>Owner</FormLabel>
                                    <Input
                                        {...register("owner", {
                                            required: "Owner is required",
                                            pattern: {
                                                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                                message: "Invalid email address",
                                            },
                                        })}
                                        type="email"
                                        placeholder="team@company.com"
                                    />
                                    <FormErrorMessage>{errors.owner?.message}</FormErrorMessage>
                                </FormControl>

                                <FormControl isRequired isInvalid={!!errors.region}>
                                    <FormLabel>Region</FormLabel>
                                    <Select {...register("region", { required: "Region is required" })} isDisabled={isLoading}>
                                        {regions?.data.map(region => (
                                            <option key={region.value} value={region.value}>
                                                {region.description}
                                            </option>
                                        ))}
                                    </Select>
                                    <FormErrorMessage>{errors.region?.message}</FormErrorMessage>
                                    {error && (
                                        <Text fontSize="sm" color="red.500" mt={1}>
                                            Error loading regions
                                        </Text>
                                    )}
                                </FormControl>

                                <FormControl isInvalid={!!errors.sbu}>
                                    <FormLabel>SBU (Optional)</FormLabel>
                                    <Input {...register("sbu")} placeholder="Strategic Business Unit" />
                                    <FormErrorMessage>{errors.sbu?.message}</FormErrorMessage>
                                </FormControl>
                            </VStack>

                            <Button type="submit" variant="primary" mb="2rem" isLoading={isCreating} isDisabled={isCreating} width="100%">
                                Create API Key
                            </Button>
                        </form>
                    ) : (
                        <VStack spacing={4} mb="2rem" align="stretch">
                            <Text fontWeight="bold" color="red.500" fontSize="lg">
                                ⚠️ Save Your API Key Now
                            </Text>
                            <Text fontSize="sm">This is the only time you will see this API key. Make sure to copy it to a secure location.</Text>

                            <FormControl>
                                <FormLabel>Application Name</FormLabel>
                                <Text>{createdApiKey.name}</Text>
                            </FormControl>

                            <FormControl>
                                <FormLabel>API Key</FormLabel>
                                <Flex gap={2} align="center">
                                    <Code p={3} borderRadius="md" flex={1} wordBreak="break-all" onCopy={handleManualCopy}>
                                        {createdApiKey.apiKey}
                                    </Code>
                                    <VStack spacing={2}>
                                        <Tooltip label={copiedToClipboard ? "API key copied" : "Copy API key"} hasArrow>
                                            <IconButton
                                                aria-label="Copy API key"
                                                icon={copiedToClipboard ? <IconCheck /> : <IconCopy />}
                                                onClick={handleCopyToClipboard}
                                                colorScheme={copiedToClipboard ? "green" : "blue"}
                                                size="sm"
                                            />
                                        </Tooltip>
                                        <Tooltip label="Download as text file">
                                            <IconButton
                                                aria-label="Download"
                                                icon={<IconDownload />}
                                                onClick={handleDownloadKey}
                                                colorScheme="blue"
                                                size="sm"
                                            />
                                        </Tooltip>
                                    </VStack>
                                </Flex>
                            </FormControl>

                            <FormControl>
                                <FormLabel>Application ID</FormLabel>
                                <Text fontSize="sm" fontFamily="monospace">
                                    {createdApiKey.applicationId}
                                </Text>
                            </FormControl>

                            <FormControl>
                                <FormLabel>Owner</FormLabel>
                                <Text>{createdApiKey.owner}</Text>
                            </FormControl>

                            <HStack spacing={4} pt={4}>
                                <Button onClick={() => setHasConfirmedSave(true)} variant="outline" flex={1} isDisabled={!hasInteractedWithKey}>
                                    I have saved my key
                                </Button>
                                <Button onClick={handleConfirmSave} variant="primary" flex={1} isDisabled={!hasConfirmedSave}>
                                    Close
                                </Button>
                            </HStack>
                        </VStack>
                    )}
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};
